import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigation } from '../contexts/NavigationProvider';
import * as api from '../services/apiService';
import type { Lineup, LineupSlot, LineupDay, User } from '../types';
import ArrowLeftIcon from './icons/ArrowLeftIcon';
import PlusCircleIcon from './icons/PlusCircleIcon';
import TrashIcon from './icons/TrashIcon';
import ArrowUpIcon from './icons/ArrowUpIcon';
import ArrowDownIcon from './icons/ArrowDownIcon';

const loadImage = (src: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });
};

const handleImageUpload = (file: File): Promise<string> => {
    return new Promise(async (resolve, reject) => {
        try {
            const originalUrl = await new Promise<string>((res, rej) => {
                const reader = new FileReader();
                reader.onload = () => res(reader.result as string);
                reader.onerror = rej;
                reader.readAsDataURL(file);
            });

            const img = await loadImage(originalUrl);

            const tempCanvas = document.createElement('canvas');
            const tempCtx = tempCanvas.getContext('2d');
            if (!tempCtx) {
                resolve(originalUrl); // Fallback to uncompressed
                return;
            }

            const MAX_DIMENSION = 800; // Compress artist photos
            let { width, height } = img;
            if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
                if (width > height) {
                    height = (height / width) * MAX_DIMENSION;
                    width = MAX_DIMENSION;
                } else {
                    width = (width / height) * MAX_DIMENSION;
                    height = MAX_DIMENSION;
                }
            }
            tempCanvas.width = width;
            tempCanvas.height = height;
            tempCtx.drawImage(img, 0, 0, width, height);
            const compressedUrl = tempCanvas.toDataURL('image/jpeg', 0.85); // 85% quality
            resolve(compressedUrl);

        } catch (error) {
            reject(error);
        }
    });
};

const sanitizeArtistForLineup = (artist: User): LineupSlot['artist'] => ({
    name: artist.name,
    handle: artist.handle,
    avatarUrl: artist.avatarUrl,
});

const LineupEditorPage: React.FC = () => {
    const { viewingOpportunity, handleNavigation, handleViewFlyer, bands } = useNavigation();
    const [lineup, setLineup] = useState<Partial<Lineup> | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const availablePerformers = useMemo(() => {
        if (!viewingOpportunity?.applications) return [];
    
        const performers = new Map<string, { name: string; cleanName: string; artist: User; avatarUrl: string }>();
    
        viewingOpportunity.applications
            .filter(app => app.status !== 'draft')
            .forEach(app => {
                if (app.applyingAs?.individual) {
                    performers.set(app.applicant.handle, { 
                        name: app.applicant.name, 
                        cleanName: app.applicant.name,
                        artist: app.applicant,
                        avatarUrl: app.applicant.avatarUrl,
                    });
                }
                app.applyingAs?.bands?.forEach(band => {
                    const bandId = `band-${band.id}`;
                    if (!performers.has(bandId)) {
                         const fullBand = bands.find(b => b.id === band.id);
                        performers.set(bandId, { 
                            name: `${band.name} (applied by ${app.applicant.name})`, 
                            cleanName: band.name,
                            artist: app.applicant,
                            avatarUrl: fullBand?.avatarUrl || app.applicant.avatarUrl,
                        });
                    }
                });
            });
    
        return Array.from(performers.entries()).map(([id, data]) => ({ id, ...data }));
    }, [viewingOpportunity, bands]);


    useEffect(() => {
        if (!viewingOpportunity) {
            setError('No opportunity selected.');
            setLoading(false);
            return;
        }

        const fetchLineup = async () => {
            try {
                const data = await api.getLineupForOpportunity(viewingOpportunity.id);
                setLineup(data || {
                    opportunityId: viewingOpportunity.id,
                    eventDays: 1,
                    defaultTransitionTime: 15,
                    days: [],
                });
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to fetch lineup data.');
            } finally {
                setLoading(false);
            }
        };

        fetchLineup();
    }, [viewingOpportunity]);

    const updateLineup = (updater: (prev: Partial<Lineup>) => Partial<Lineup>) => {
        setLineup(prev => prev ? updater(prev) : null);
    };

    const handleSave = async () => {
        if (!lineup || !viewingOpportunity) return;
        setIsSaving(true);
        setError('');
        try {
            await api.saveLineup(viewingOpportunity.id, lineup);
            alert('Lineup saved successfully!');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save lineup.');
        } finally {
            setIsSaving(false);
        }
    };

    const handlePrint = () => {
        window.print();
    };
    
    if (loading) return <p className="text-brand-muted">Loading lineup editor...</p>;
    if (error) return <p className="text-red-400">Error: {error}</p>;
    if (!viewingOpportunity || !lineup) return null;

    return (
        <div className="space-y-6 lineup-editor-container">
            <style>{`
                @media print {
                    body { background: white !important; color: black !important; }
                    .no-print, header, nav, footer, aside, button { display: none !important; }
                    .print-only { display: block !important; }
                    .lineup-editor-container { padding: 0 !important; margin: 0 !important; }
                    .bg-brand-surface, .bg-gray-800\\/50 { background: transparent !important; border: 1px solid #ddd !important; }
                    .text-white { color: black !important; }
                    .text-brand-orange { color: #FF4D00 !important; }
                    .text-brand-muted { color: #666 !important; }
                    input, select, textarea { border: none !important; background: transparent !important; color: black !important; -webkit-appearance: none; }
                    .day-schedule-card { break-inside: avoid; margin-bottom: 20px; border: 1px solid #000; padding: 10px; }
                    .slot-card { border-bottom: 1px dashed #ccc; padding-bottom: 5px; }
                }
            `}</style>

            <div className="no-print">
                <button onClick={() => handleNavigation('myLineups')} className="flex items-center space-x-2 text-brand-muted hover:text-white font-semibold">
                    <ArrowLeftIcon className="h-5 w-5" />
                    <span>Back to My Lineups</span>
                </button>
            </div>

            <div className="bg-brand-surface rounded-lg p-6 border border-gray-800">
                <h2 className="text-3xl font-bold text-white uppercase tracking-tight">Lineup Editor</h2>
                <p className="text-xl text-brand-orange font-bold uppercase">{viewingOpportunity.title}</p>
                <div className="mt-4 flex flex-wrap gap-2 no-print">
                    <button onClick={handlePrint} className="bg-gray-700 text-white font-bold px-4 py-2 rounded-full hover:bg-gray-600 transition-colors text-xs uppercase tracking-widest border border-gray-600">
                        Print Lineup
                    </button>
                    <button onClick={() => handleViewFlyer(lineup, viewingOpportunity)} className="bg-blue-600 text-white font-bold px-4 py-2 rounded-full hover:bg-blue-500 transition-colors text-xs uppercase tracking-widest">
                        Generate Flyer
                    </button>
                </div>
            </div>

            <div className="bg-brand-surface rounded-lg p-6 space-y-4 no-print border border-gray-800">
                <h3 className="text-xl font-bold text-white uppercase tracking-tight">Event Settings</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <FormInput label="Number of Days" type="number" value={lineup.eventDays || 1} onChange={e => updateLineup(l => ({ ...l, eventDays: parseInt(e.target.value, 10) || 1 }))} />
                    </div>
                    <div>
                        <FormInput label="Default Transition Time (minutes)" type="number" value={lineup.defaultTransitionTime || 15} onChange={e => updateLineup(l => ({ ...l, defaultTransitionTime: parseInt(e.target.value, 10) || 0 }))} />
                    </div>
                </div>
            </div>

            <div className="space-y-6">
                {[...Array(lineup.eventDays || 1)].map((_, index) => (
                    <DaySchedule
                        key={index}
                        dayIndex={index}
                        lineup={lineup}
                        updateLineup={updateLineup}
                        availablePerformers={availablePerformers}
                    />
                ))}
            </div>

            <div className="flex justify-end items-center p-4 no-print">
                <button onClick={handleSave} disabled={isSaving} className="bg-brand-orange text-white font-black px-8 py-3 rounded-full hover:bg-orange-600 transition-all shadow-lg shadow-brand-orange/20 uppercase tracking-widest text-sm disabled:opacity-50">
                    {isSaving ? 'Saving...' : 'Save Lineup'}
                </button>
            </div>
        </div>
    );
};

interface DayScheduleProps {
    dayIndex: number;
    lineup: Partial<Lineup>;
    updateLineup: (updater: (prev: Partial<Lineup>) => Partial<Lineup>) => void;
    availablePerformers: { id: string; name: string; cleanName: string; artist: User; avatarUrl: string }[];
}

const DaySchedule: React.FC<DayScheduleProps> = ({ dayIndex, lineup, updateLineup, availablePerformers }) => {
    const day = lineup.days?.[dayIndex] || { date: '', slots: [] };
    const artistImageInputRefs = useRef<(HTMLInputElement | null)[]>([]);

    const handleDayChange = (field: keyof LineupDay, value: any) => {
        updateLineup(l => {
            const newDays = [...(l.days || [])];
            newDays[dayIndex] = { ...day, [field]: value };
            return { ...l, days: newDays };
        });
    };
    
    const handleSlotChange = (slotIndex: number, updatedSlot: LineupSlot) => {
        let newSlots = [...day.slots];
        
        // If 'isMain' is being set to true, unset it for all other slots in the same day.
        if (updatedSlot.isMain) {
            newSlots = newSlots.map((slot, index) => ({
                ...slot,
                isMain: index === slotIndex,
            }));
        }
        newSlots[slotIndex] = updatedSlot;

        handleDayChange('slots', newSlots);
    };

    const onArtistImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, slotIndex: number) => {
        if (e.target.files && e.target.files[0]) {
            const dataUrl = await handleImageUpload(e.target.files[0]);
            handleSlotChange(slotIndex, { ...day.slots[slotIndex], artistImageUrl: dataUrl });
        }
    };

    const addSlot = () => {
        if (availablePerformers.length === 0) {
            alert("No applicants available to add to the lineup.");
            return;
        }
        const firstPerformer = availablePerformers[0];
        const newSlot: LineupSlot = {
            id: Date.now().toString(),
            artist: { ...sanitizeArtistForLineup(firstPerformer.artist), name: firstPerformer.cleanName },
            artistImageUrl: firstPerformer.avatarUrl,
            startTime: '12:00',
            endTime: '13:00',
            transitionTimeAfter: lineup.defaultTransitionTime || 15,
            isMain: false,
        };
        handleDayChange('slots', [...day.slots, newSlot]);
    };
    
    const removeSlot = (slotIndex: number) => {
        handleDayChange('slots', day.slots.filter((_, i) => i !== slotIndex));
    };

    const moveSlot = (index: number, direction: 'up' | 'down') => {
        const newSlots = [...day.slots];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= newSlots.length) return;
        [newSlots[index], newSlots[targetIndex]] = [newSlots[targetIndex], newSlots[index]];
        handleDayChange('slots', newSlots);
    };

    return (
        <div className="bg-brand-surface rounded-lg p-6 day-schedule-card border border-gray-800">
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-black text-white uppercase tracking-tight">Day {dayIndex + 1}</h3>
                <input type="date" value={day.date} onChange={e => handleDayChange('date', e.target.value)} className="bg-gray-700 text-white rounded-md p-2 text-sm focus:outline-none font-bold"/>
            </div>
            <div className="space-y-4">
                {day.slots.map((slot, i) => (
                    <div key={slot.id} className="bg-gray-800/50 p-4 rounded-xl flex items-start gap-4 slot-card border border-gray-700">
                        <div className="flex flex-col gap-2 pt-2 no-print">
                           <button onClick={() => moveSlot(i, 'up')} disabled={i === 0} className="disabled:opacity-20 hover:text-brand-orange transition-colors"><ArrowUpIcon className="h-5 w-5" /></button>
                           <button onClick={() => moveSlot(i, 'down')} disabled={i === day.slots.length - 1} className="disabled:opacity-20 hover:text-brand-orange transition-colors"><ArrowDownIcon className="h-5 w-5" /></button>
                        </div>
                        <div className="flex-grow grid grid-cols-1 md:grid-cols-4 gap-4 items-start">
                            <div className="md:col-span-2 space-y-2">
                                <div>
                                    <label className="block text-[10px] font-black text-brand-muted mb-1 uppercase tracking-widest">Artist / Project</label>
                                    <select 
                                        value={availablePerformers.find(p => p.cleanName === slot.artist.name)?.id || ''} 
                                        onChange={e => {
                                            const newPerformer = availablePerformers.find(a => a.id === e.target.value);
                                            if (newPerformer) {
                                                const artistForSlot = {
                                                    ...sanitizeArtistForLineup(newPerformer.artist),
                                                    name: newPerformer.cleanName
                                                };
                                                handleSlotChange(i, {...slot, artist: artistForSlot, artistImageUrl: newPerformer.avatarUrl })
                                            }
                                        }} className="w-full bg-gray-700 text-white rounded-md p-2 text-sm focus:outline-none font-bold">
                                        {availablePerformers.map(performer => <option key={performer.id} value={performer.id}>{performer.name}</option>)}
                                    </select>
                                </div>
                                <div className="flex items-center justify-between">
                                    <button onClick={() => artistImageInputRefs.current[i]?.click()} className="bg-gray-600 text-[10px] font-black p-2 rounded-md uppercase tracking-widest no-print">
                                        {slot.artistImageUrl ? 'Change Photo' : 'Upload Photo'}
                                    </button>
                                    {slot.artistImageUrl && (
                                        <img src={slot.artistImageUrl} alt="Artist preview" className="h-10 w-10 rounded-md object-cover border border-gray-600" />
                                    )}
                                    <input type="file" ref={el => { if (el) artistImageInputRefs.current[i] = el; }} onChange={e => onArtistImageUpload(e, i)} accept="image/*" className="hidden"/>
                                    <label className="flex items-center gap-2 text-sm text-gray-300 no-print font-bold">
                                        <input
                                            type="checkbox"
                                            checked={!!slot.isMain}
                                            onChange={e => handleSlotChange(i, { ...slot, isMain: e.target.checked })}
                                            className="h-4 w-4 rounded bg-gray-600 border-gray-500 text-brand-orange focus:ring-brand-orange"
                                        />
                                        Main Artist
                                    </label>
                                    <span className="hidden print-only font-black uppercase text-xs">
                                        {slot.isMain ? '(Main Event)' : ''}
                                    </span>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <FormInput label="Start" type="time" value={slot.startTime} onChange={e => handleSlotChange(i, { ...slot, startTime: e.target.value })} />
                                <FormInput label="End" type="time" value={slot.endTime} onChange={e => handleSlotChange(i, { ...slot, endTime: e.target.value })} />
                            </div>
                            <div className="md:col-span-1">
                                <FormInput label="Transition (min)" type="number" value={slot.transitionTimeAfter} onChange={e => handleSlotChange(i, { ...slot, transitionTimeAfter: parseInt(e.target.value, 10) })} />
                            </div>
                        </div>
                        <button onClick={() => removeSlot(i)} className="pt-2 no-print text-red-400 hover:text-red-300 transition-colors" title="Remove slot">
                            <TrashIcon className="h-6 w-6" />
                        </button>
                    </div>
                ))}
                <button onClick={addSlot} className="flex items-center gap-2 text-brand-orange hover:underline font-black uppercase tracking-widest text-[10px] no-print">
                    <PlusCircleIcon className="h-5 w-5" /> Add Performance Slot
                </button>
            </div>
        </div>
    );
};

const FormInput: React.FC<React.InputHTMLAttributes<HTMLInputElement> & { label: string }> = ({ label, ...props }) => (
    <div>
        <label className="block text-[10px] font-black text-brand-muted mb-1 uppercase tracking-widest">{label}</label>
        <input {...props} className="w-full bg-gray-700 text-white rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-orange font-bold" />
    </div>
);


export default LineupEditorPage;